import { TransactionView } from "@common/types/views/transactionViews";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Button } from "@design-system/Button/Button";
import {
    DEFAULT_FILTERS,
    FiltersChangeFn,
    TransactionsFilters,
} from "@components/transactions/filters/lib";
import { WORKSPACE_USER_CACHE_KEYS } from "@hooks/useWorkspaceUserCache";
import { CloseIcon } from "@icons";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import { isEqual } from "@common/helpers/comparison";
import { omitBy } from "@common/helpers/objects";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    type ActiveFilter,
    FilterTabs,
} from "@components/pageHeader/filters/lib";
import { ViewConfig } from "@lib/views";
import { TransactionSortValue } from "@components/transactions/useSort";
import { Drawer } from "@design-system/Drawer/Drawer";
import {
    MatchingRuleData,
    type PaginatedTransactionsActionsContextValue,
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import { TransactionDetailsContainer } from "@components/transactions/TransactionDetails/TransactionDetailsContainer";
import { TransactionsView } from "@components/transactions/views/TransactionsView";
import { TransactionsViewFooterProps } from "@components/transactions/views/TransactionsViewFooter";
import styles from "./TransactionViewDrawer.module.scss";

export interface TransactionViewDrawerProps {
    view: TransactionView;
    open: boolean;
    onClose: () => void;
    headerAddon?: React.ReactNode | (() => React.ReactNode);
    parentFilters?: TransactionsFilters;
    cacheKey?: WORKSPACE_USER_CACHE_KEYS;
    emptyView?: () => React.ReactNode;
    footerAddon?: (options?: TransactionsViewFooterProps) => React.ReactNode;
    replaceBulkActionsWith?: (
        options?: TransactionsViewFooterProps,
    ) => React.ReactNode;
    selectAllOnOpen?: boolean;
    actionComponent?: () => React.ReactNode;
    prependContent?: React.ReactNode;
    viewConfigOverride?: ViewConfig<
        TransactionListItem,
        string,
        TransactionSortValue
    >;
    contentBetweenHeaderAndTable?: React.ReactNode;
    contentClassName?: string;
    matchingRuleData?: MatchingRuleData;
    availableFilters?: FilterTabs[];
    directionSwitchReadonly?: boolean;
    prioritizedActiveFilters?: ActiveFilter[];
    disabledActiveFilters?: ActiveFilter[];
    // When provided, renders the `TablePageHeader` filter row even if there are
    // no `availableFilters`, and renders these static chips alongside active
    // filters. Used by the similar-transactions drawer for non-removable scope
    // filters like similar-to and entity.
    staticFilterPill?: React.ReactNode;
    /** Open details for the first loaded row (used when pointing at one trx). */
    autoSelectFirst?: boolean;
    /** Open details for this id immediately, without waiting for the list. */
    initialSelectedTransactionId?: number;
    shouldSuggestSimilarTransactionsUpdate?: boolean;
    filterRowActions?: (options: {
        currentView: TransactionView;
        onViewChange: PaginatedTransactionsActionsContextValue["setCurrentView"];
    }) => React.ReactNode;
}

const emptyFilters = {};

const TransactionViewDrawerContent: React.FC<TransactionViewDrawerProps> = ({
    headerAddon,
    onClose,
    emptyView,
    footerAddon,
    selectAllOnOpen,
    actionComponent,
    prependContent,
    viewConfigOverride,
    contentBetweenHeaderAndTable,
    contentClassName,
    availableFilters,
    directionSwitchReadonly,
    prioritizedActiveFilters,
    disabledActiveFilters,
    staticFilterPill,
    filterRowActions,
    autoSelectFirst,
    initialSelectedTransactionId,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [selectedTransactionId, setSelectedTransactionId] = useState<
        number | undefined
    >(initialSelectedTransactionId);
    const hasAutoSelectedRef = useRef(initialSelectedTransactionId != null);

    const {
        currentView,
        transactionCount,
        filters: currentFilters,
        transactions,
    } = useContext(PaginatedTransactionsDataContext);
    const { setCurrentFilters, setCurrentView, setPage } = useContext(
        PaginatedTransactionsActionsContext,
    );

    useEffect(() => {
        if (
            !autoSelectFirst ||
            hasAutoSelectedRef.current ||
            selectedTransactionId != null ||
            !transactions?.length
        ) {
            return;
        }
        hasAutoSelectedRef.current = true;
        setSelectedTransactionId(transactions[0].id);
    }, [autoSelectFirst, selectedTransactionId, transactions]);

    const header = useMemo(() => {
        if (typeof headerAddon === "function") {
            return headerAddon();
        }
        return headerAddon;
    }, [headerAddon]);

    const isFilterBarVisible = !!availableFilters?.length || !!staticFilterPill;

    const currentFiltersRef = useRef(currentFilters);
    currentFiltersRef.current = currentFilters;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const combined: TransactionsFilters = {
                ...currentFiltersRef.current,
                ...value,
            };
            const merged = omitBy(
                combined,
                (v) => v === null || v === undefined,
            );

            if (isEqual(merged, currentFiltersRef.current)) {
                return;
            }

            setPage(1);
            setCurrentFilters(merged);
        },
        [setCurrentFilters, setPage],
    );

    const handleSearchChange = useCallback(
        (value: string) => {
            handleFiltersChange({ search: value });
        },
        [handleFiltersChange],
    );

    const renderedFilterRowActions = useMemo(
        () =>
            filterRowActions?.({
                currentView,
                onViewChange: setCurrentView,
            }),
        [currentView, filterRowActions, setCurrentView],
    );

    return (
        <div className={styles.wrapper}>
            <header className={styles.header}>
                <div className={styles.headerAddon}>{header}</div>
                <Tooltip content="Close" shortcut="escape" side="bottom">
                    <Button
                        iconOnly
                        variant="secondary"
                        size="md"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </Button>
                </Tooltip>
            </header>
            {isFilterBarVisible && (
                <TablePageHeader
                    filters={currentFilters}
                    onFiltersChange={handleFiltersChange}
                    availableFilters={availableFilters}
                    defaultFilters={DEFAULT_FILTERS}
                    search={currentFilters.search ?? ""}
                    onSearchChange={handleSearchChange}
                    staticActiveFilters={staticFilterPill}
                    directionSwitchReadonly={directionSwitchReadonly}
                    prioritizedActiveFilters={prioritizedActiveFilters}
                    disabledActiveFilters={disabledActiveFilters}
                    isFullPage
                    borderBottom
                    hideClearButton
                >
                    {renderedFilterRowActions}
                </TablePageHeader>
            )}
            {contentBetweenHeaderAndTable}
            <div
                className={[styles.content, contentClassName]
                    .filter(Boolean)
                    .join(" ")}
                ref={scrollContainerRef}
            >
                <TransactionsBulkActionsContextProvider
                    currentFilters={currentView?.filters ?? emptyFilters}
                    onUpdated={() => {}}
                    totalTransactions={transactionCount}
                >
                    <TransactionsView
                        view={currentView}
                        selectedTransactionId={selectedTransactionId}
                        setSelectedTransactionId={(id) =>
                            setSelectedTransactionId(id as number)
                        }
                        emptyView={emptyView}
                        footer={footerAddon}
                        selectAllOnOpen={selectAllOnOpen}
                        actionComponent={actionComponent}
                        prependContent={prependContent}
                        viewConfigOverride={viewConfigOverride}
                    />
                </TransactionsBulkActionsContextProvider>

                {selectedTransactionId && (
                    <TransactionDetailsContainer
                        id={selectedTransactionId}
                        onClose={() => setSelectedTransactionId(undefined)}
                    />
                )}
            </div>
        </div>
    );
};

const TransactionViewDrawerInstance: React.FC<
    Omit<TransactionViewDrawerProps, "open"> & { onClose: () => void }
> = ({
    headerAddon,
    parentFilters,
    matchingRuleData,
    onClose,
    shouldSuggestSimilarTransactionsUpdate,
    ...rest
}) => {
    const [page, setPage] = useState(1);

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={rest.view}
            paginationMode={{ type: "pages", page, setPage }}
            parentFilters={parentFilters}
            cacheKey={rest.cacheKey}
            matchingRuleData={matchingRuleData}
            disableSimilarTransactionsUpdate={
                !shouldSuggestSimilarTransactionsUpdate
            }
        >
            <TransactionViewDrawerContent
                open
                onClose={onClose}
                headerAddon={headerAddon}
                parentFilters={parentFilters}
                {...rest}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};

export const TransactionViewDrawer: React.FC<TransactionViewDrawerProps> = ({
    open,
    onClose,
    headerAddon,
    parentFilters,
    matchingRuleData,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const shouldShowContent = isOpen || open;
    const instanceKey =
        rest.initialSelectedTransactionId ??
        parentFilters?.transactionIds?.join(",") ??
        "drawer";

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            onAnimationEnd={setIsOpen}
            renderHeader={false}
            noPadding
        >
            {shouldShowContent && (
                <TransactionViewDrawerInstance
                    key={instanceKey}
                    onClose={onClose}
                    headerAddon={headerAddon}
                    parentFilters={parentFilters}
                    matchingRuleData={matchingRuleData}
                    {...rest}
                />
            )}
        </Drawer>
    );
};
